import { useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import type { ColumnDef } from "@tanstack/react-table";
import { Table } from "@common/Table";
import { Title, Button, Badge, ErrorState } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useLegalClassRestrictionApi } from "@/hooks/apiHooks/useLegalClassRestrictionApi";
import { useMountRead } from "@/hooks/useMountRead";
import { legalClassRestrictionsAtom } from "@/store/legalClassRestrictionAtoms";
import type { LegalClassRestriction } from "@models/legalClassRestriction";
import { legalClassLabel } from "@models/product";
import { RuleStatusBadge } from "./_components/RuleStatusBadge";
import { RestrictionFormDialog } from "./_components/RestrictionFormDialog";
import { DeleteRestrictionDialog } from "./_components/DeleteRestrictionDialog";
import {
    describeDestination,
    formatRestrictionDate,
    getRestrictionLifecycle,
} from "./_helpers/restrictionStatus";

// Open dialog, if any. `{ mode: "edit" }` with a null restriction is not
// representable, so the form dialog always knows which of the two it is.
type Dialog =
    | { kind: "create" }
    | { kind: "edit"; restriction: LegalClassRestriction }
    | { kind: "delete"; restriction: LegalClassRestriction };

// How often the screen re-reads the clock. Every boundary here is a day
// boundary, so a minute is ample precision — and browsers already clamp
// background timers to roughly this interval anyway.
const CLOCK_TICK_MS = 60_000;

export const LegalClasses = () => {
    const { getLegalClassRestrictions } = useLegalClassRestrictionApi();
    const restrictions = useAtomValue(legalClassRestrictionsAtom);
    const [dialog, setDialog] = useState<Dialog | null>(null);
    // The status of the read THIS mount issued, never an app-scoped error
    // atom: one written by a page and never reset outlives the unmount, so
    // returning here after a failed load painted "Couldn't load the
    // restrictions" before a request had even been issued (#300). It also
    // tells "the read has not answered yet" from "it answered with nothing",
    // so the empty state ("nothing is restricted") cannot flash before the
    // request is made. Every read, the first and each re-read after a save or
    // a Retry, starts at "loading", and only the latest one settles it: a
    // save's re-read behind a slower read must not let the older answer win.
    const { readStatus, reload: fetchRestrictions } = useMountRead(
        getLegalClassRestrictions,
    );
    // The lifecycle badge answers "is this rule blocking checkout right now?",
    // so it cannot be a snapshot from whenever the table last rendered — a
    // monitoring surface left open overnight would keep showing "Scheduled"
    // for a rule that has since started blocking. One clock for the whole
    // screen: the delete confirmation reads the same `now`, so it can never
    // contradict the badge that prompted the click.
    const [now, setNow] = useState(() => new Date());

    // A tick rather than focus/visibility listeners: the case that goes wrong
    // is a tab left VISIBLE and untouched, where neither event ever fires.
    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const columns = useMemo<ColumnDef<LegalClassRestriction>[]>(
        () => [
            {
                accessorKey: "legalClass",
                header: "Legal Class",
                enableSorting: false,
                cell: ({ row }) => legalClassLabel(row.original.legalClass),
            },
            {
                id: "destination",
                header: "Applies To",
                enableSorting: false,
                cell: ({ row }) => describeDestination(row.original),
            },
            {
                accessorKey: "isLegal",
                header: "Effect",
                enableSorting: false,
                cell: ({ row }) =>
                    row.original.isLegal ? (
                        <Badge
                            color={ComponentColor.Info}
                            size={ComponentSize.SM}
                        >
                            Allowed
                        </Badge>
                    ) : (
                        <Badge
                            color={ComponentColor.Error}
                            size={ComponentSize.SM}
                        >
                            Restricted
                        </Badge>
                    ),
            },
            {
                accessorKey: "startDate",
                header: "Starts (UTC)",
                enableSorting: false,
                cell: ({ row }) => formatRestrictionDate(row.original.startDate),
            },
            {
                accessorKey: "endDate",
                // The window is half-open: the rule is NOT in force on its end
                // date. "Ends" alone reads as the last day covered.
                header: "Ends (UTC, excluded)",
                enableSorting: false,
                cell: ({ row }) => formatRestrictionDate(row.original.endDate),
            },
            {
                id: "status",
                header: "Status",
                enableSorting: false,
                cell: ({ row }) => (
                    <RuleStatusBadge
                        lifecycle={getRestrictionLifecycle(row.original, now)}
                    />
                ),
            },
            {
                id: "actions",
                header: "",
                enableSorting: false,
                cell: ({ row }) => {
                    // Every row shows the same "Edit" and "Delete", so each
                    // button's accessible name carries the row it acts on.
                    const summary = `${legalClassLabel(row.original.legalClass)} in ${describeDestination(row.original)}`;
                    return (
                        <div className="flex gap-2">
                            <Button
                                color={ComponentColor.Secondary}
                                size={ComponentSize.XS}
                                aria-label={`Edit ${summary}`}
                                onClick={() =>
                                    setDialog({
                                        kind: "edit",
                                        restriction: row.original,
                                    })
                                }
                            >
                                Edit
                            </Button>
                            <Button
                                color={ComponentColor.Error}
                                size={ComponentSize.XS}
                                aria-label={`Delete ${summary}`}
                                onClick={() =>
                                    setDialog({
                                        kind: "delete",
                                        restriction: row.original,
                                    })
                                }
                            >
                                Delete
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [now],
    );

    const isLoading = readStatus === "loading";
    // One element, reused by the two branches that show rows. The spinner goes
    // INSIDE the table body: a page-level one unmounted the whole table on
    // every save and delete, so the page collapsed, re-expanded and lost its
    // scroll position.
    const table = (
        <Table
            data={restrictions}
            columns={columns}
            size={ComponentSize.XS}
            isLoading={isLoading}
        />
    );

    return (
        <div className="px-10">
            <div className="grid grid-cols-12 items-center gap-4">
                <Title>Legal Class Restrictions</Title>
                <div className="col-span-1 mt-1">
                    <Button
                        color={ComponentColor.Secondary}
                        size={ComponentSize.SM}
                        onClick={() => setDialog({ kind: "create" })}
                    >
                        + New
                    </Button>
                </div>
            </div>
            <p className="text-neutral/80 max-w-3xl">
                These rules decide where each legal class may ship. A restricted
                class is hidden from browse and search, has its pricing blanked
                on the product page, and is refused at checkout for addresses in
                the state or ZIP code named here.
            </p>

            {isLoading ? (
                // Ahead of the failure branch so a retry gives feedback, and
                // so the empty copy cannot paint over a read that has not
                // answered — on this screen that frame reads as "nothing is
                // restricted".
                table
            ) : readStatus === "failed" ? (
                // A failed list is not an empty one, and it is not the rows the
                // last successful read left behind either. This screen governs
                // legal compliance, so it must never answer "nothing is
                // restricted" — or answer with stale rows — when the truth is
                // "we could not find out".
                <ErrorState
                    title="Couldn't load the restrictions"
                    message="The restriction list is unavailable, so what is restricted right now is unknown. This is not the same as there being no restrictions."
                    onRetry={fetchRestrictions}
                />
            ) : restrictions.length > 0 ? (
                table
            ) : (
                <div className="text-center text-neutral p-8">
                    <p className="font-semibold">
                        No restrictions are configured.
                    </p>
                    <p className="text-neutral/80">
                        Every legal class can currently ship to every state and
                        ZIP code.
                    </p>
                </div>
            )}

            {dialog?.kind === "create" && (
                <RestrictionFormDialog
                    restriction={null}
                    onClose={() => setDialog(null)}
                    onSaved={fetchRestrictions}
                />
            )}
            {dialog?.kind === "edit" && (
                <RestrictionFormDialog
                    key={dialog.restriction.id}
                    restriction={dialog.restriction}
                    onClose={() => setDialog(null)}
                    onSaved={fetchRestrictions}
                />
            )}
            {dialog?.kind === "delete" && (
                <DeleteRestrictionDialog
                    key={dialog.restriction.id}
                    restriction={dialog.restriction}
                    now={now}
                    onClose={() => setDialog(null)}
                    onDeleted={fetchRestrictions}
                />
            )}
        </div>
    );
};
